import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { ArrowLeft, Warning, FileArrowUp } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ContactSearch from '../components/ui/ContactSearch';
import InvitationLinkDialog from '../components/shared/InvitationLinkDialog';
import { ETAPES_CHANTIER, PHASES_CABINET } from '../lib/libelles';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * B1 (P1#11-12) — Création courte : nom, adresse, client (nom, e-mail, téléphone, ou
 * client existant), étape et budget travaux HT. Le reste (équipe, dates, pièces) se fait
 * plus tard, guidé par « Pour bien démarrer » sur la synthèse du chantier.
 * B14 — l'e-mail du client n'ouvre plus l'accès tout seul : « Inviter le client » est
 * une case explicite (décochée par défaut, et en prospection).
 */
const ETAPES_CREATION = [
  { cle: 'prospection', libelle: 'Prospection (mission pas encore signée)' },
  ...ETAPES_CHANTIER.filter((e) => e.cle !== 'cloture').map((e) => ({ cle: e.cle, libelle: e.libelle })),
];

export default function NewChantier() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [lien, setLien] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    owner_name: '',
    owner_contact: '',
    owner_telephone: '',
    etape: 'prospection',
    current_phase: '',
    budget: '',
    inviter_client: false,
  });

  const etapeRef = ETAPES_CHANTIER.find((e) => e.cle === (formData.etape === 'prospection' ? 'etudes' : formData.etape));
  const phasesEtape = (etapeRef?.phases || []);
  const emailClient = (formData.owner_contact || '').trim();

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const payload = {
        name: formData.name,
        address: formData.address,
        owner_name: formData.owner_name,
        owner_contact: emailClient || null,
        owner_telephone: formData.owner_telephone || null,
        etape: formData.etape,
        current_phase: formData.current_phase || null,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        inviter_client: !!(formData.inviter_client && emailClient),
      };

      const { data } = await axios.post(`${API_URL}/api/chantiers`, payload, { withCredentials: true });
      toast.success('Chantier créé');
      const inv = data.invitation_client;
      if (inv && !inv.email_sent && inv.activation_url) {
        // Même message que pour un collaborateur : l'e-mail n'est pas parti, voici le lien.
        toast.warning("Client invité, mais l'e-mail n'est pas parti");
        setLien({ ...inv, user_name: formData.owner_name, user_email: emailClient, _chantierId: data.id });
        return;
      }
      if (inv && !inv.email_sent) {
        toast.warning("L'e-mail d'accès au client n'est pas parti : prévenez-le qu'il peut se connecter avec son adresse habituelle.");
      } else if (inv) {
        toast.success(`Invitation envoyée à ${emailClient}`);
      }
      navigate(`/chantiers/${data.id}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Erreur lors de la création');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 max-w-3xl" data-testid="new-chantier-page">
        <Link to="/chantiers" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6">
          <ArrowLeft size={16} />
          Retour aux chantiers
        </Link>

        <h1 className="text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Nouveau chantier
        </h1>
        <p className="text-sm text-[#6B6B6B] mb-8">
          L'essentiel suffit pour commencer. L'équipe, les dates et les pièces se complètent ensuite depuis la synthèse du chantier.
        </p>

        {/* Option : importer un dossier existant */}
        <div className="mb-8 p-5 border border-[#C75B39]/30 bg-[#FFF2EB]/40 flex flex-col sm:flex-row items-start gap-4" data-testid="import-suggestion-banner">
          <div className="w-11 h-11 flex-shrink-0 bg-[#C75B39] text-white flex items-center justify-center">
            <FileArrowUp size={22} weight="bold" />
          </div>
          <div className="flex-1">
            <h2 className="text-base font-semibold text-slate-900">Vous avez déjà un dossier en cours ?</h2>
            <p className="text-sm text-slate-600 mt-1">
              Importez vos documents existants (DPGF, comptes rendus, planning…) : l'application pré-remplit le chantier à votre place.
            </p>
          </div>
          <Link to="/chantiers/import">
            <Button type="button" className="bg-[#C75B39] hover:bg-[#A94B2E] rounded-none whitespace-nowrap" data-testid="import-existing-btn">
              Importer un dossier existant
            </Button>
          </Link>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 flex items-start gap-3">
            <Warning size={20} weight="bold" className="text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="card-swiss space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Label htmlFor="name">Nom du chantier *</Label>
              <Input id="name" name="name" value={formData.name} onChange={handleChange}
                placeholder="Ex : Réhabilitation 129 rue Gabriel-Péri" required
                className="mt-1 rounded-none border-slate-300" data-testid="chantier-name-input" />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="address">Adresse *</Label>
              <Input id="address" name="address" value={formData.address} onChange={handleChange}
                placeholder="Ex : 129 rue Gabriel-Péri, 93200 Saint-Denis" required
                className="mt-1 rounded-none border-slate-300" data-testid="chantier-address-input" />
            </div>

            <div>
              <Label>Étape *</Label>
              <Select value={formData.etape} onValueChange={(v) => setFormData(prev => ({
                ...prev, etape: v, current_phase: '',
                // B14 — en prospection, le client n'est pas invité par défaut
                inviter_client: v === 'prospection' ? false : prev.inviter_client,
              }))}>
                <SelectTrigger className="mt-1 rounded-none border-slate-300" data-testid="chantier-etape-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ETAPES_CREATION.map((e) => <SelectItem key={e.cle} value={e.cle}>{e.libelle}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Phase (facultatif)</Label>
              <Select value={formData.current_phase || 'aucune'} onValueChange={(v) => setFormData(prev => ({ ...prev, current_phase: v === 'aucune' ? '' : v }))}>
                <SelectTrigger className="mt-1 rounded-none border-slate-300" data-testid="chantier-phase-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="aucune">Non précisée</SelectItem>
                  {phasesEtape.map((p) => <SelectItem key={p} value={p}>{p} — {PHASES_CABINET[p] || p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="budget">Budget travaux (€ HT)</Label>
              <Input id="budget" name="budget" type="number" min="0" value={formData.budget} onChange={handleChange}
                placeholder="Ex : 150000" className="mt-1 rounded-none border-slate-300 md:w-1/2" data-testid="chantier-budget-input" />
            </div>
          </div>

          {/* Client (maître d'ouvrage) */}
          <fieldset className="border border-[#E0E0DB] p-4 space-y-4" data-testid="chantier-client-bloc">
            <legend className="px-1 text-sm font-semibold text-[#2C2C2C]">Client (maître d'ouvrage)</legend>
            <div>
              <Label className="text-xs text-[#6B6B6B]">Client déjà connu ?</Label>
              <ContactSearch
                sources={['users']}
                userRoleFilter="client"
                placeholder="Tapez un nom ou un e-mail…"
                onSelect={(c) => { if (c) setFormData(prev => ({ ...prev, owner_name: c.name || '', owner_contact: c.email || '', owner_telephone: c.telephone || '' })); }}
                onCreateNew={(q) => setFormData(prev => ({ ...prev, owner_name: q }))}
                testId="chantier-client-search"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="owner_name">Nom</Label>
                <Input id="owner_name" name="owner_name" value={formData.owner_name} onChange={handleChange}
                  placeholder="M. et Mme Durand" className="mt-1 rounded-none border-slate-300" data-testid="chantier-owner-input" />
              </div>
              <div>
                <Label htmlFor="owner_contact">E-mail</Label>
                <Input id="owner_contact" name="owner_contact" type="email" value={formData.owner_contact} onChange={handleChange}
                  placeholder="client@exemple.fr" className="mt-1 rounded-none border-slate-300" data-testid="chantier-owner-email" />
              </div>
              <div>
                <Label htmlFor="owner_telephone">Téléphone</Label>
                <Input id="owner_telephone" name="owner_telephone" value={formData.owner_telephone} onChange={handleChange}
                  placeholder="06…" className="mt-1 rounded-none border-slate-300" data-testid="chantier-owner-telephone" />
              </div>
            </div>
            <label className={`flex items-start gap-2 text-sm ${emailClient ? 'text-[#2C2C2C]' : 'text-[#9A9A9A]'}`}>
              <input
                type="checkbox"
                className="mt-1"
                checked={!!formData.inviter_client && !!emailClient}
                disabled={!emailClient}
                onChange={(e) => setFormData(prev => ({ ...prev, inviter_client: e.target.checked }))}
                data-testid="chantier-inviter-client"
              />
              <span>
                Inviter le client à suivre le chantier
                <span className="block text-xs text-[#6B6B6B]">
                  Il reçoit un e-mail avec un lien personnel. Sans invitation, il n'a pas accès au chantier
                  {formData.etape === 'prospection' ? ' (conseillé : attendre la signature de la mission).' : '.'}
                </span>
              </span>
            </label>
          </fieldset>

          <div className="flex items-center justify-end gap-4 pt-4 border-t border-slate-200">
            <Link to="/chantiers">
              <Button type="button" variant="outline" className="rounded-none">Annuler</Button>
            </Link>
            <Button type="submit" disabled={isLoading} className="bg-[#0047FF] hover:bg-[#0035CC] rounded-none" data-testid="create-chantier-button">
              {isLoading ? 'Création…' : 'Créer le chantier'}
            </Button>
          </div>
        </form>
      </div>
      <InvitationLinkDialog
        invitation={lien}
        onClose={() => { const cid = lien?._chantierId; setLien(null); if (cid) navigate(`/chantiers/${cid}`); }}
      />
    </Layout>
  );
}
